// Canvas2D crosshair renderer.
//
// The canvas is sized to the config's conservative bounds (once per config /
// DPR change), then layers are drawn around the centre. Only animated frames
// redraw — the HUD decides when.

import { SHAPE_DEFS } from "./shapes";
import type { CrosshairConfig, ParamValue } from "./types";
import type { LayerAnim } from "./animate";

const MAX_SIDE = 512;

/** Conservative half-extent in CSS px (covers rotation + max pulse scale). */
export function configBounds(cfg: CrosshairConfig): number {
  let half = 4;
  for (const layer of cfg.layers) {
    if (!layer.visible) continue;
    const def = SHAPE_DEFS[layer.shape];
    const ext = def.extent(layer.params) * layer.scale * cfg.scale;
    let pulse = 1;
    for (const fx of layer.fx) pulse = Math.max(pulse, 1 + Math.max(0, fx.scale));
    const off = Math.max(Math.abs(layer.offset.x), Math.abs(layer.offset.y));
    half = Math.max(half, ext * pulse + off + layer.outline.thickness + 2);
  }
  return Math.min(MAX_SIDE / 2, Math.ceil(half));
}

export function baseAnims(cfg: CrosshairConfig): LayerAnim[] {
  return cfg.layers.map((layer) => ({
    scale: 1,
    rotate: 0,
    gap: typeof layer.params.gap === "number" ? layer.params.gap : 0,
    thickness: typeof layer.params.thickness === "number" ? layer.params.thickness : 2,
    offsetX: 0,
    offsetY: 0,
    opacity: layer.opacity,
    color: layer.color,
    outlineColor: layer.outline.color,
  }));
}

/** Draw the crosshair centred on the current origin. */
export function drawScene(
  ctx: CanvasRenderingContext2D,
  cfg: CrosshairConfig,
  anims: LayerAnim[],
  dpr: number,
): void {
  cfg.layers.forEach((layer, i) => {
    drawLayer(ctx, cfg, layer, anims[i] ?? baseAnims(cfg)[i], dpr);
  });
}

function drawLayer(
  ctx: CanvasRenderingContext2D,
  cfg: CrosshairConfig,
  layer: CrosshairConfig["layers"][number],
  a: LayerAnim,
  dpr: number,
): void {
  if (!layer.visible || layer.opacity <= 0) return;
  const def = SHAPE_DEFS[layer.shape];
  const params: Record<string, ParamValue> = { ...layer.params };
  if ("gap" in params) params.gap = Math.max(0, a.gap);
  if ("thickness" in params) params.thickness = Math.max(0.1, a.thickness);

  const rot = layer.rotation + a.rotate;
  const scale = layer.scale * cfg.scale * a.scale;
  ctx.save();
  ctx.globalAlpha = Math.min(1, Math.max(0, layer.opacity * a.opacity));
  ctx.globalCompositeOperation = layer.blend === "additive" ? "lighter" : "source-over";

  // Pixel alignment: axis-aligned, integral scale/offset only, and an odd
  // number of *device* pixels across the stroke (the classic 1 px blur).
  const axisAligned = ((rot % 90) + 90) % 90 === 0;
  const integral =
    Math.abs(scale - Math.round(scale)) < 0.01 &&
    Math.abs(layer.offset.x - Math.round(layer.offset.x)) < 0.01 &&
    Math.abs(layer.offset.y - Math.round(layer.offset.y)) < 0.01;
  if (cfg.crisp && axisAligned && integral) {
    const devicePx = Math.round(a.thickness * scale * dpr);
    if (devicePx % 2 === 1) ctx.translate(0.5 / dpr, 0.5 / dpr);
  }

  ctx.translate(layer.offset.x + a.offsetX, layer.offset.y + a.offsetY);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.scale(scale, scale);

  const filled = def.filled(params);
  const baseW = def.width(params);
  const outline = layer.outline.thickness;
  const persist = layer.outline.mode === "persistent" ? 1 / Math.max(0.05, a.scale) : 1;
  ctx.lineCap = def.round(params) ? "round" : "butt";
  ctx.lineJoin = def.round(params) ? "round" : "miter";

  if (outline > 0) {
    ctx.beginPath();
    def.draw(ctx, params);
    ctx.strokeStyle = a.outlineColor;
    ctx.lineWidth = (filled ? outline * 2 : baseW + outline * 2) * persist;
    ctx.stroke();
  }

  ctx.beginPath();
  def.draw(ctx, params);
  if (filled) {
    ctx.fillStyle = a.color;
    ctx.fill();
  } else {
    ctx.strokeStyle = a.color;
    ctx.lineWidth = Math.max(0.1, baseW);
    ctx.stroke();
  }
  ctx.restore();
}

export class CrosshairRenderer {
  private cfg: CrosshairConfig;
  private side = 0;
  private dpr = 1;
  private fit = 1;
  private ctx: CanvasRenderingContext2D | null;

  constructor(private canvas: HTMLCanvasElement, cfg: CrosshairConfig) {
    this.cfg = cfg;
    this.ctx = canvas.getContext("2d");
  }

  setConfig(cfg: CrosshairConfig): void {
    this.cfg = cfg;
    this.layout();
  }

  /** Resize the backing store for the current config + DPR. Returns CSS side. */
  layout(dpr = window.devicePixelRatio || 1): number {
    const half = configBounds(this.cfg);
    const side = Math.max(8, half * 2);
    this.side = side;
    this.dpr = Math.max(0.5, dpr);
    this.fit = Math.min(1, MAX_SIDE / side);
    const css = Math.max(2, Math.round(side * this.fit));
    this.canvas.style.width = `${css}px`;
    this.canvas.style.height = `${css}px`;
    this.canvas.width = Math.max(1, Math.round(css * this.dpr));
    this.canvas.height = Math.max(1, Math.round(css * this.dpr));
    return css;
  }

  get cssSide(): number {
    return this.side ? Math.max(2, Math.round(this.side * this.fit)) : 0;
  }

  render(anims: LayerAnim[]): void {
    const ctx = this.ctx;
    if (!ctx) return;
    if (!this.side) this.layout();
    const dpr = this.canvas.width / Math.max(1, this.cssSide);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(dpr * this.fit, 0, 0, dpr * this.fit, 0, 0);
    ctx.translate(this.side / 2, this.side / 2);
    drawScene(ctx, this.cfg, anims, dpr * this.fit);
  }
}

/** Render a config to a data URL (slot thumbnails). */
export function renderThumbnail(cfg: CrosshairConfig, size: number, dpr = 1): string {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(size * dpr));
  c.height = Math.max(1, Math.round(size * dpr));
  const ctx = c.getContext("2d");
  if (!ctx) return "";
  const half = Math.max(1, configBounds(cfg));
  const fit = size / (half * 2);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, size, size);
  ctx.translate(size / 2, size / 2);
  ctx.scale(fit, fit);
  drawScene(ctx, cfg, baseAnims(cfg), dpr * fit);
  return c.toDataURL("image/png");
}
